import { noop } from 'lodash';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';

import { Box } from '../../components/Box/Box';
import { Button } from '../../components/Button/Button';
import { CollapsibleText } from '../../components/CollapsibleText/CollapsibleText';
import { DateText } from '../../components/DateText/DateText';
import { Icon } from '../../components/Icon/Icon';
import { IconButton } from '../../components/IconButton/IconButton';
import { Layout } from '../../components/Layout/Layout';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { Quote } from '../../components/Quote/Quote';
import { Text } from '../../components/Text/Text';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { User } from '../../components/User/User';
import { HtmlEntityType } from '../../enums/HtmlEntityType';
import { useElapse } from '../../hooks/useElapse';
import { Action } from '../../types/Action';
import { EntityResolvers } from '../../types/EntityResolvers';
import { MentionedEntity } from '../../types/MentionedEntity';
import { Mentions } from '../../types/Mentions';
import { User as UserType } from '../../types/User';
import { Uuid } from '../../types/Uuid';
import { serializeContentAst } from '../../utils/contentAst';
import { TextEditor } from '../TextEditor/TextEditor';
import { CommentActions } from './CommentActions';
import { CommentDraft } from './CommentDraft';
import { Comment, CommentData, MentionEntityType } from './types';
import { getHighlightFromContext, resolveHighlightEntity } from './utils';

const IS_NEW_TIME_WINDOW = 15 * 1000; // 15 seconds

interface Props {
  comment: Comment;
  entityResolvers: EntityResolvers;
  mentions: Mentions;
  isActive: boolean;
  isLoading: boolean;
  isMentionsLoading: boolean;
  resolvedMentions: Mentions;
  /** Collapse comment if possible */
  shouldCollapseComment?: boolean;
  viewer: UserType;
  enableReply: boolean;
  /** Copy link action is called */
  onCopyLink: (comment: Comment) => void;
  /** Delete a given comment */
  onDelete: (comment: Comment) => void;
  /** Select a comment to reply to */
  onReply: (comment: Comment) => void;
  /** Update a comment */
  onUpdate: (comment: Comment) => void;
  onResolveComment: (commentData: Comment) => void;
  onUnresolveComment: (commentData: Comment) => void;
  onFilterAction?: (action: Action, comment: Comment) => Action;
  onHighlightClick?: (highlight: Uuid) => void;
  /** This callback is fired when mentioning.
   * @param mention the mentioned entity
   */
  onMention?: (mention: MentionedEntity<MentionEntityType>) => void;
  /** Callback for searching async mentions */
  onMentionSearch?: (updatedSearch: string) => void;
}

export const CommentPost = ({
  comment,
  enableReply,
  entityResolvers,
  isActive,
  isLoading,
  isMentionsLoading,
  mentions,
  resolvedMentions,
  shouldCollapseComment,
  viewer,
  onCopyLink,
  onDelete,
  onFilterAction,
  onHighlightClick,
  onMention,
  onMentionSearch,
  onReply,
  onResolveComment,
  onUnresolveComment,
  onUpdate,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const {
    content,
    createdBy,
    createdDate,
    entity,
    id,
    isResolved,
    modifiedDate,
    replies,
    threadId,
  } = comment;

  const styles = useStyles();

  const [isEditing, setIsEditing] = useState(false);

  const [hasTimeWindowElapsed] = useElapse(IS_NEW_TIME_WINDOW);
  const serializedContent = useMemo(
    () => serializeContentAst(content),
    [content],
  );

  const highlightEntity = getHighlightFromContext(comment.context);

  const resolvedHighlight = resolveHighlightEntity(
    highlightEntity,
    entityResolvers,
  );

  const isNew =
    Date.now() - createdDate.getTime() < IS_NEW_TIME_WINDOW &&
    !hasTimeWindowElapsed;

  const isEdited = modifiedDate > createdDate;

  const isReply = !!threadId;

  const getUserInfo = () => {
    let info = formatDate(createdDate, 'relative');

    if (isResolved) {
      info = intl.formatMessage(
        {
          id: 'CommentPost.resolved',
          defaultMessage: '{info} {entityTypeBullet} Resolved',
          description:
            'Label for resolved comment. Example: info = "2 hours ago", entityTypeBullet = "•"',
        },
        {
          info,
          entityTypeBullet: HtmlEntityType.Bullet,
        },
      );
    }

    return info;
  };

  const handleUpdate = (updatedComment: CommentData) => {
    onUpdate({
      ...comment,
      content: updatedComment.content,
      mentions: updatedComment.mentions,
    });
    setIsEditing(false);
  };

  return (
    <Layout
      align="flex-start"
      direction="column"
      id={id}
      role="comment" // eslint-disable-line jsx-a11y/aria-role -- TODO: EKP-42656
      position="relative"
      px={6}
      py={4}
      spacing={3}
      styles={[
        styles.colors.status,
        isEditing ? null : componentStyles.container,
      ]}
      styleProps={{
        status: isNew || isActive || isEditing ? 'active' : undefined,
      }}
    >
      {isNew && (
        <Layout position="absolute" px={2} styles={componentStyles.newTag}>
          <Text color="text.secondary">
            {intl.formatMessage({
              id: 'CommentPost.newComment',
              defaultMessage: 'New comment',
              description: 'Label for new comment tag',
            })}
          </Text>
        </Layout>
      )}
      <Layout align="flex-start" justify="space-between" w="100%">
        <Layout>
          <User
            options={{
              infoTooltip: formatDate(createdDate, 'short_datetime'),
              info: getUserInfo(),
            }}
            placeholder={intl.formatMessage({
              id: 'CommentPost.userPlaceholder',
              defaultMessage: 'Deactivated User',
              description: 'Placeholder for deactivated user',
            })}
            //a dummy user object is created upstream. check if user object has name to show placeholder
            user={
              createdBy.firstName && createdBy.lastName ? createdBy : undefined
            }
            mode="info"
          />
          {comment.isCommentEmbedded !== undefined && (
            <Layout alignSelf="start">
              <Icon
                tooltip={
                  comment.isCommentEmbedded
                    ? intl.formatMessage({
                        id: 'CommentPost.visibleToAnyone',
                        defaultMessage:
                          'This comment thread is visible to anyone with access to the document.',
                        description: 'Tooltip for comment visibility - public',
                      })
                    : intl.formatMessage({
                        id: 'CommentPost.visibleToTicketUsers',
                        defaultMessage:
                          'This comment thread is only visible to your users that can see this ticket.',
                        description: 'Tooltip for comment visibility - private',
                      })
                }
                icon={comment.isCommentEmbedded ? 'globe' : 'lock'}
              />
            </Layout>
          )}
        </Layout>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <Layout preset="icons">
            {enableReply && (
              <IconButton
                icon="reply"
                tooltip={intl.formatMessage({
                  id: 'CommentPost.reply',
                  defaultMessage: 'Reply',
                  description: 'Tooltip for reply action',
                })}
                onClick={() => onReply(comment)}
              />
            )}
            <Box>
              <CommentActions
                comment={comment}
                viewer={viewer}
                onDelete={onDelete}
                onCopyLink={onCopyLink}
                onResolveComment={onResolveComment}
                onUpdate={() => setIsEditing(true)}
                onUnresolveComment={onUnresolveComment}
                onFilterAction={onFilterAction}
              />
            </Box>
          </Layout>
        )}
      </Layout>
      {!isReply && highlightEntity && (
        <Box
          w="100%"
          onClick={() =>
            resolvedHighlight
              ? onHighlightClick?.(resolvedHighlight.id)
              : noop()
          }
        >
          <Quote>
            {resolvedHighlight?.text || (
              <Layout justify="center">
                <LoadingSpinner />
              </Layout>
            )}
          </Quote>
        </Box>
      )}
      {isEditing ? (
        <CommentDraft
          content={content}
          entity={entity}
          isFocused={true}
          isMentionsLoading={isMentionsLoading}
          mentions={mentions}
          resolvedMentions={resolvedMentions}
          onSubmit={handleUpdate}
          onMention={onMention}
          onCancel={() => setIsEditing(false)}
          onMentionSearch={onMentionSearch}
        />
      ) : (
        <Text w="100%">
          <CollapsibleText
            limit={200}
            fullContent={
              <>
                <TextEditor
                  content={content}
                  isMentionsLoading={isMentionsLoading}
                  mentions={mentions}
                  mode="preview"
                  resolvedMentions={resolvedMentions}
                  onChange={noop}
                  onPost={noop}
                />
                {isEdited && (
                  <Tooltip
                    tooltip={
                      <DateText date={modifiedDate} format="short_datetime" />
                    }
                  >
                    <Text color="text.quiet" ml={2}>
                      {intl.formatMessage({
                        id: 'CommentPost.edited',
                        defaultMessage: '(Edited)',
                        description: 'Label for edited comment',
                      })}
                    </Text>
                  </Tooltip>
                )}
              </>
            }
            shouldResetToInitialState={shouldCollapseComment}
            text={serializedContent}
          />
        </Text>
      )}
      {enableReply && replies > 0 && (
        <Button
          text={intl.formatMessage(
            {
              id: 'CommentPost.viewReplies',
              defaultMessage: '{replies, plural, one {Reply} other {Replies}}',
              description:
                'Button text for viewing replies. Example: replies = "3"',
            },
            {
              replies,
            },
          )}
          variant="action"
          onClick={() => onReply(comment)}
        />
      )}
    </Layout>
  );
};

const componentStyles = {
  container: {
    ':hover': {
      backgroundColor: 'background.quiet',
    },
  },
  newTag: {
    top: -26,
    right: 0,
    backgroundColor: 'background.quiet',
  },
};
